import { FaqItem, Faqable } from 'src/types';
import AccordionFaq from 'src/components/AccordionFaq';
import { AdminRoutes } from 'src/lib/routes';
import useConfirmDialog from 'src/hooks/useConfirmDialog';
import ApiClient from 'src/lib/api_client';
import dayjs from 'dayjs'
import { FiPlus, FiMinus } from 'react-icons/fi';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import styles from 'src/styles/modules/components/user_faq.module.scss';
import { useLocale, useTranslations } from 'next-intl';
import { LOCALE_DATE_FORMAT } from 'src/lib/constants';

type Props = {
  isAdmin: boolean;
  faq: FaqItem;
  faqable: Faqable;
  containerClass?: string;
  showAnwser?: boolean;
  onClickEdit?: (faq: FaqItem) => void;
  onDelete?: (faq: FaqItem) => void;
};

const UserFaqItem: React.FC<Props> = ({ faq, faqable, containerClass, onClickEdit, onDelete, isAdmin, showAnwser }) => {
  const locale = useLocale();
  const translation = useTranslations('components.UserFaqItem')
  const UIDateFormat = LOCALE_DATE_FORMAT[locale].UIDateFormat
  const deleteFaq = useEnhanceApiClient(ApiClient.deleteFaq);
  const confirmDialog = useConfirmDialog();
  const editUrl =
     faqable.type === 'Blog'
      ? AdminRoutes.adminEditBlogRoute({ id: faqable.id }).toUrl()
      : faqable.type === 'SupplierActivityOption'
      ? AdminRoutes.adminOptionRoute({ id: faqable.id }).toUrl()
      : faqable.type === 'Collection'
      ? AdminRoutes.editAdminCollectionRoute({ id: faqable.id }).toUrl()
      : faqable.type === 'City'
      ? AdminRoutes.editAdminCityAdvancedSettingsRoute({ city_id: faqable.id }).toUrl()
      : faqable.type === 'Country'
      ? AdminRoutes.editAdminCountryAdvancedSettingsRoute({ country_id: faqable.id }).toUrl()
      : ''
  return (
    <AccordionFaq
      className={containerClass}
      defaultShow={showAnwser}
      title={faq.question}
      openIcon={<FiPlus strokeWidth={2} size={24} className="tw-text-brand-navy" />}
      hideIcon={<FiMinus strokeWidth={2} size={24} className="tw-text-brand-navy" />}
      renderTitle={() => (
        <div className="tw-mr-4">
          <h3 className="tw-mb-0 tw-font-semibold tw-font-styleSans md:tw-text-xl tw-text-lg tw-leading-none md:tw-leading-none">{faq.question}</h3> {faq.username && (
            <div className="tw-text-sm tw-text-grey-light">
              {`${faq.username}${
                faq.createdAt ? `, ${dayjs(faq.createdAt).format(UIDateFormat)}` : ''
              }`}
            </div>
          )}
        </div>
      )}
      keyName={faq.id}
    >
      <div>
        <div className={styles.faqContent}>
          {faq.answer ? (
            <div dangerouslySetInnerHTML={{ __html: faq.formattedAnswer || '' }} />
          ) : (
            <span className="tw-text-grey-light">{translation('noReplyYet')}.</span>
          )}
        </div>

        {isAdmin && (
          <div className="tw-mt-4">
            {faq.username ? (
              <>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    onClickEdit && onClickEdit(faq);
                  }}
                >
                  {faq.answer ? translation('edit') : translation('reply')}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary tw-ml-2"
                  onClick={() => {
                    confirmDialog({
                      title: translation('deleteFaq'),
                      body: translation('areYouSureToDelete'),
                      okText: translation('delete'),
                      cancelText: translation('cancel'),
                    }).then(() => {
                      deleteFaq({ id: faq.id }).then(() => {
                        onDelete && onDelete(faq);
                      });
                    });
                  }}
                >
                  {translation('delete')}
                </button>
              </>
            ) : (
              <>
                <a href={editUrl} className="btn btn-primary" target="_blank" rel="noreferrer">
                  {translation('edit')}
                </a>
                <a href={editUrl} className="btn btn-secondary tw-ml-2" target="_blank" rel="noreferrer">
                  {translation('delete')}
                </a>
              </>
            )}
          </div>
        )}
      </div>
    </AccordionFaq>
  );
};

export default UserFaqItem;
